import { useMemo } from 'react'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { FlightsClassTypes } from 'constants/flight'
import useCharterDateRangesByMonth from 'hooks/CharterFlights/useCharterDateRangesByMonth'
import { EmptyArray } from 'lib/array/arrayUtils'
import { useCalendarItemData } from './useCalendarItemData'

interface UseNextAvailableCharterPeriodParams {
  charterBundleRoute: App.CharterBundleRoute
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  priceMode: 'total' | 'perPerson'
}

interface NextAvailablePeriod {
  checkIn: string
  checkOut: string
  nights: number
}

const DEFAULT_NIGHTS = 5

/**
 * Hook to find the next available charter bundle period
 * Checks flight and offer availability for each period
 * Uses duration from dateRange if available, otherwise defaults to 5 nights
 */
export function useNextAvailableCharterPeriod(params: UseNextAvailableCharterPeriodParams): NextAvailablePeriod | undefined {
  const { charterBundleRoute, rooms, fareClass, priceMode } = params

  // Get all available date ranges
  const { dateRangesByMonth } = useCharterDateRangesByMonth({
    charterBundleRoute,
    occupancies: rooms ?? EmptyArray,
    fareClass,
    disabled: (rooms ?? EmptyArray).length === 0,
  })

  // Flatten and sort all date ranges by checkIn date (future dates first)
  const sortedDateRanges = useMemo(() => {
    const today = moment().startOf('day')
    const allRanges = Object.values(dateRangesByMonth).flat()

    return allRanges
      .filter(range => {
        const checkInMoment = moment(range.checkIn).startOf('day')
        return checkInMoment.isSameOrAfter(today)
      })
      .sort((a, b) => {
        return moment(a.checkIn).valueOf() - moment(b.checkIn).valueOf()
      })
  }, [dateRangesByMonth])

  // Check availability for the first few date ranges (limit to avoid too many hook calls)
  const firstRange = sortedDateRanges[0]
  const secondRange = sortedDateRanges[1]
  const thirdRange = sortedDateRanges[2]

  // Use nights from dateRange, or default to 5 if not available
  const firstNights = firstRange?.nights ?? DEFAULT_NIGHTS
  const firstCheckOut = firstRange?.checkOut
    ? firstRange.checkOut
    : firstRange?.checkIn
      ? moment(firstRange.checkIn).add(firstNights, 'days').format(ISO_DATE_FORMAT)
      : undefined

  const secondNights = secondRange?.nights ?? DEFAULT_NIGHTS
  const secondCheckOut = secondRange?.checkOut
    ? secondRange.checkOut
    : secondRange?.checkIn
      ? moment(secondRange.checkIn).add(secondNights, 'days').format(ISO_DATE_FORMAT)
      : undefined

  const thirdNights = thirdRange?.nights ?? DEFAULT_NIGHTS
  const thirdCheckOut = thirdRange?.checkOut
    ? thirdRange.checkOut
    : thirdRange?.checkIn
      ? moment(thirdRange.checkIn).add(thirdNights, 'days').format(ISO_DATE_FORMAT)
      : undefined

  // Check availability for first range
  const firstBundleData = useCalendarItemData({
    checkIn: firstRange?.checkIn ?? '',
    checkOut: firstCheckOut ?? '',
    departDate: firstRange?.departureDate ?? '',
    returnDate: firstRange?.returnDate ?? '',
    nights: firstNights,
    rooms: rooms ?? [],
    fareClass,
    priceMode,
    charterBundleRoute,
  })

  // Check availability for second range
  const secondBundleData = useCalendarItemData({
    checkIn: secondRange?.checkIn ?? '',
    checkOut: secondCheckOut ?? '',
    departDate: secondRange?.departureDate ?? '',
    returnDate: secondRange?.returnDate ?? '',
    nights: secondNights,
    rooms: rooms ?? [],
    fareClass,
    priceMode,
    charterBundleRoute,
  })

  // Check availability for third range
  const thirdBundleData = useCalendarItemData({
    checkIn: thirdRange?.checkIn ?? '',
    checkOut: thirdCheckOut ?? '',
    departDate: thirdRange?.departureDate ?? '',
    returnDate: thirdRange?.returnDate ?? '',
    nights: thirdNights,
    rooms: rooms ?? [],
    fareClass,
    priceMode,
    charterBundleRoute,
  })

  // Find first available period
  return useMemo(() => {
    // Check first range if available and data is ready
    if (firstRange && firstCheckOut && !firstBundleData.fetching) {
      if (firstBundleData.available) {
        return {
          checkIn: firstRange.checkIn,
          checkOut: firstCheckOut,
          nights: firstNights,
        }
      }
    }

    // Check second range if first is not available
    if (secondRange && secondCheckOut && !secondBundleData.fetching) {
      if (secondBundleData.available) {
        return {
          checkIn: secondRange.checkIn,
          checkOut: secondCheckOut,
          nights: secondNights,
        }
      }
    }

    // Check third range if first two are not available
    if (thirdRange && thirdCheckOut && !thirdBundleData.fetching) {
      if (thirdBundleData.available) {
        return {
          checkIn: thirdRange.checkIn,
          checkOut: thirdCheckOut,
          nights: thirdNights,
        }
      }
    }

    // If still fetching or no available period found in first three, return first range as fallback
    // This ensures we always have a default date even if availability check is still pending
    if (firstRange && firstCheckOut) {
      return {
        checkIn: firstRange.checkIn,
        checkOut: firstCheckOut,
        nights: firstNights,
      }
    }

    return undefined
  }, [
    firstRange,
    firstCheckOut,
    firstBundleData,
    firstNights,
    secondRange,
    secondCheckOut,
    secondBundleData,
    secondNights,
    thirdRange,
    thirdCheckOut,
    thirdBundleData,
    thirdNights,
  ])
}
